import * as React from "react"
import { FloatingActionButton, CircularProgress, IconButton, FontIcon } from "material-ui"
import ContentAdd from "material-ui/svg-icons/content/add"
import AddLeaveRequestDialog from "../../../components/leave_tracking/AddLeaveRequestDialog"
import autobind from "class-autobind"
import UserStore from "../../../stores/UserStore"
import { ETimeoffValidationType, ITimeoffSearch, ILeaveTrackingStateFilter, ITimeoffRequestDocument, IDateRange, ETimeoffStatus, IUserDocument, ELoggedInUserType, EListCalendarViewModes, IHolidayInstanceForPunch } from "../../../models"
import * as api from "../../../api"
import strings from "../../../localization/strings"
import TableActionBar from "../../../components/layout/TableActionBar"
import LeaveTrackingFilter from "../../../components/leave_tracking/LeaveTrackingFilter"
import UserDetailLeaveTrackingTable from "./UserDetailLeaveTrackingTable"
import LeaveTrackingNotesDrawer from "../../../components/leave_tracking/LeaveTrackingNotesDrawer"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import * as userActions from "../../../actions/UserActions"
import ChooseListCalendarViewMode from "../../../components/common/ChooseListCalendarViewMode"
import LeaveTrackingFilterForCalendarView from "../../../components/leave_tracking/LeaveTrackingFilterForCalendarView"
import { LeaveTrackingCalendarView } from "../../../components/leave_tracking/calendar_view"
import moment from "moment"
import * as timeHelper from "../../../helpers/timeHelper"
import _ from "lodash"
import AppStateStore from "../../../stores/AppStateStore"
import * as leaveTrackingActions from "../../../actions/LeaveTrackingActions"
import * as utils from "../../../helpers/utils"
import { EnumValues } from "enum-values"
import * as reportAction from "../../../actions/ReportAction"
import * as userHelper from "../../../helpers/userHelper"
import AccountStore from "../../../stores/AccountStore"

interface IProps {
  employee: IUserDocument
  loggedInUserType: ELoggedInUserType
  holidays: IHolidayInstanceForPunch[]
}
interface IState {
  viewMode: EListCalendarViewModes
  stateFilter: ILeaveTrackingStateFilter
  referenceDateForCalendarView: Date | string
  dateFilter: IDateRange
  openLeaveRequest: boolean
  editLeaveRequest: ITimeoffRequestDocument
  disableRequestFields: boolean
  searchText: string
  selectedLeaveRequests: ITimeoffRequestDocument[]
  leaveRequestForDrawerId: string
  leaveRequestsSearch: ITimeoffSearch
  loading: boolean
  total: number
  offset: number
  limit: number
}

export default class UserDetailLeaveTracking extends React.Component<IProps, IState> {
  constructor(props) {
    super(props)
    const storedViewModeName = utils.getCookie("ViewModeForLeaveTrackingPage")
    this.state = {
      viewMode: storedViewModeName ? EListCalendarViewModes[storedViewModeName] : EListCalendarViewModes.ListView,
      stateFilter: UserStore.getLeaveTrackingStateFilter(),
      referenceDateForCalendarView: moment().format(),
      dateFilter: UserStore.getLeaveTrackingDateFilter(),
      openLeaveRequest: false,
      selectedLeaveRequests: [],
      editLeaveRequest: null,
      disableRequestFields: false,
      searchText: "",
      leaveRequestsSearch: null,
      leaveRequestForDrawerId: null,
      loading: true,
      total: 0,
      offset: 0,
      limit: 0,
    }
    autobind(this)
  }

  componentDidMount() {
    this.getLeaveRequests()
    UserStore.on("change", this.changeEvent)
    UserStore.on("change_require_add_new_leave_tracking", this.changeOpenReaveRequest)
  }

  componentWillUnmount() {
    UserStore.removeListener("change", this.changeEvent)
    UserStore.removeListener("change_require_add_new_leave_tracking", this.changeOpenReaveRequest)
  }

  changeOpenReaveRequest() {
    UserStore.getRequireAddNewLeaveTracking() &&
      this.setState({ openLeaveRequest: true, editLeaveRequest: null })
  }

  changeEvent() {
    const changed =
      !_.isEqual(this.state.stateFilter, UserStore.getLeaveTrackingStateFilter()) ||
      !_.isEqual(this.state.dateFilter, UserStore.getLeaveTrackingDateFilter()) ||
      !_.isEqual(this.state.searchText, UserStore.getLeaveTrackingSearchText()) ||
      !_.isEqual(this.state.offset, UserStore.getLeaveTrackingOffset()) ||
      !_.isEqual(this.state.limit, UserStore.getLeaveTrackingLimit())
    changed &&
      this.setState({
        stateFilter: UserStore.getLeaveTrackingStateFilter(),
        dateFilter: UserStore.getLeaveTrackingDateFilter(),
        searchText: UserStore.getLeaveTrackingSearchText(),
        offset: UserStore.getLeaveTrackingOffset(),
        limit: UserStore.getLeaveTrackingLimit(),
      }, () => this.getLeaveRequests())
  }

  getLeaveRequests() {
    const statusList: ETimeoffStatus[] = []
    Object.keys(this.state.stateFilter).forEach((key) => {
      this.state.stateFilter[key] && statusList.push(ETimeoffStatus[key])
    })

    if (statusList.length === 0) {
      this.setState({
        selectedLeaveRequests: [],
        editLeaveRequest: null,
        leaveRequestsSearch: {
          TotalDocumentCount: 0,
          Documents: [],
        }
      })
    }
    else {
      this.setState({
        loading: true,
        selectedLeaveRequests: [],
        editLeaveRequest: null,
      }, () => {
        api.getEmployeeLeaveRequests(
          this.props.employee.Id,
          this.state.viewMode === EListCalendarViewModes.ListView ? this.state.dateFilter.StartDate : moment(this.state.referenceDateForCalendarView).utc(true).startOf("month").format(),
          this.state.viewMode === EListCalendarViewModes.ListView ? this.state.dateFilter.EndDate : moment(this.state.referenceDateForCalendarView).utc(true).endOf("month").format(),
          this.state.viewMode === EListCalendarViewModes.ListView ? this.state.offset / this.state.limit : null,
          this.state.viewMode === EListCalendarViewModes.ListView ? this.state.limit : null,
          this.state.searchText,
          statusList
        )
          .then((res) => this.setState({ loading: false, leaveRequestsSearch: res.data }))
      })
    }
  }

  handleAdd(leaveRequest, successMessage) {
    return new Promise<ETimeoffValidationType>(resolve =>
      api.addLeaveRequest(leaveRequest, userHelper.isESSAction(this.props.employee.Id)).then(res => {
        switch (res.data) {
          case ETimeoffValidationType.None:
            userActions.setReloadSelfServiceLeaveDashboard()
            AppStateStore.setProgressSnackbarContent(successMessage)
            this.getLeaveRequests()
            resolve(ETimeoffValidationType.None)
            break
          case ETimeoffValidationType.DateRangeError:
            resolve(ETimeoffValidationType.DateRangeError)
            break
          case ETimeoffValidationType.BalanceError:
            resolve(ETimeoffValidationType.BalanceError)
            break
          case ETimeoffValidationType.DuplicatedDateError:
            resolve(ETimeoffValidationType.DuplicatedDateError)
            break
          case ETimeoffValidationType.ProhibitionEndDateError:
            resolve(ETimeoffValidationType.ProhibitionEndDateError)
            break
          case ETimeoffValidationType.MaxPunchSetLengthError:
            resolve(ETimeoffValidationType.MaxPunchSetLengthError)
            break
          case ETimeoffValidationType.PayTypeError:
            resolve(ETimeoffValidationType.PayTypeError)
            break
          case ETimeoffValidationType.LockedError:
            resolve(ETimeoffValidationType.LockedError)
            break
          case ETimeoffValidationType.EmployeeInactive:
            resolve(ETimeoffValidationType.EmployeeInactive)
            break
        }
      })
    )
  }

  update(leaveRequest, successMessage) {
    return new Promise<ETimeoffValidationType>(resolve =>
      api.updateLeaveRequest(leaveRequest, userHelper.isESSAction(this.props.employee.Id)).then(res => {
        switch (res.data) {
          case ETimeoffValidationType.None:
            userActions.setReloadSelfServiceLeaveDashboard()
            AppStateStore.setProgressSnackbarContent(successMessage)
            this.getLeaveRequests()
            resolve(ETimeoffValidationType.None)
            break
          case ETimeoffValidationType.DateRangeError:
            resolve(ETimeoffValidationType.DateRangeError)
            break
          case ETimeoffValidationType.BalanceError:
            resolve(ETimeoffValidationType.BalanceError)
            break
          case ETimeoffValidationType.DuplicatedDateError:
            resolve(ETimeoffValidationType.DuplicatedDateError)
            break
          case ETimeoffValidationType.ProhibitionEndDateError:
            resolve(ETimeoffValidationType.ProhibitionEndDateError)
            break
          case ETimeoffValidationType.MaxPunchSetLengthError:
            resolve(ETimeoffValidationType.MaxPunchSetLengthError)
            break
          case ETimeoffValidationType.PayTypeError:
            resolve(ETimeoffValidationType.PayTypeError)
            break
          case ETimeoffValidationType.LockedError:
            resolve(ETimeoffValidationType.LockedError)
            break
          case ETimeoffValidationType.EmployeeInactive:
            resolve(ETimeoffValidationType.EmployeeInactive)
            break
          case ETimeoffValidationType.PermissionError:
            resolve(ETimeoffValidationType.PermissionError)
            break
        }
      })
    )
  }

  handleUpdateLeaveRequest(leaveRequest: ITimeoffRequestDocument) {
    api.checkValidateUpdateTimeoff(leaveRequest.Id).then(res =>
      res.data ?
        this.setState({ openLeaveRequest: true, editLeaveRequest: leaveRequest, disableRequestFields: false }) :
        // AppStateStore.setProgressSnackbarContent(this.props.loggedInUserType === ELoggedInUserType.Employee ? strings.Cannot_update_none_pending_leave_request : strings.Cannot_update_leave_request_for_locked_period_or_denied_leave_request)
        this.setState({ openLeaveRequest: true, editLeaveRequest: leaveRequest, disableRequestFields: true })
    )
  }

  handleDelete(selectedLeaveRequest: ITimeoffRequestDocument[]) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.deleteLeaveRequests(selectedLeaveRequest.map(item => item.Id), userHelper.isESSAction(this.props.employee.Id))
      .then(res => {
        switch (res.data) {
          case "Cannot_delete_approved_leave_request":
            AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_approved_leave_request)
            break
          case "Cannot_delete_leave_request_applied_in_payroll":
            AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_leave_request_applied_in_payroll)
            break
          case "Cannot_delete_denied_leave_request":
            AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_denied_leave_request)
            break
          case "Cannot_delete_approved_leave_request_for_locked_period":
            AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_approved_leave_request_for_locked_period)
            break
          case "Leave_request_deleted_successfully":
            userActions.setReloadSelfServiceLeaveDashboard()
            AppStateStore.setProgressSnackbarContent(strings.Leave_request_deleted_successfully)
            this.getLeaveRequests()
            break
        }
      })
  }

  handleUpdateLeaveRequestsStatus(status: ETimeoffStatus, leaveRequests: ITimeoffRequestDocument[]) {

    // deprecated
    // leaveRequests.length === 0 ?
    //   AppStateStore.setProgressSnackbarContent(status === ETimeoffStatus.Approved ? strings.Choose_leave_requests_to_approve : strings.Choose_leave_requests_to_deny) :
    //   AppStateStore.setConfirmDialogContent({
    //     content: status === ETimeoffStatus.Approved ? strings.Are_you_sure_you_want_to_approve_selected_leave_request : strings.Are_you_sure_you_want_to_deny_selected_leave_request,
    //     confirm: () => leaveTrackingActions.handleUpdateStatus(status, leaveRequests, this.updateStatusCb)
    //   })
    // agar approved included aa and leave request uha bhi included jehnke approve karho aan ta if main wendo
    // agar approved included and unapproved item  nahay ta else main
    if (leaveRequests.length === 0) {
      AppStateStore.setProgressSnackbarContent(status === ETimeoffStatus.Approved ? strings.Choose_leave_requests_to_approve : strings.Choose_leave_requests_to_deny)
    } else {
      const accruals = leaveRequests.map((request) => this.props.employee.Accruals.find((accrual) => request.AccrualId === accrual.Id))

      const accountAccruals = accruals.map((accrual) => AccountStore.getCurrentAccount().Accruals.find((accountAccrual) => accountAccrual.Id === accrual.LinkedAccountAccrualId))

      const approvedExists = leaveRequests.some((request) => request.Status === ETimeoffStatus.Approved)
      const noteNotexist = leaveRequests.some((request) => request.Notes.length === 0)

      if (ETimeoffStatus.Approved && accountAccruals.some((item) => item.RequireNoteOnTimeOffApproval) && !approvedExists && noteNotexist) {
        AppStateStore.setProgressSnackbarContent(strings.One_or_more_leave_request_comments_are_required)
      }

      else {
        AppStateStore.setConfirmDialogContent({
          content: status === ETimeoffStatus.Approved ? strings.Are_you_sure_you_want_to_approve_selected_leave_request : strings.Are_you_sure_you_want_to_deny_selected_leave_request,
          confirm: () => leaveTrackingActions.handleUpdateStatus(status, leaveRequests, this.updateStatusCb)
        })
      }
    }
  }

  updateStatusCb() {
    userActions.setReloadSelfServiceLeaveDashboard()
    this.getLeaveRequests()
  }

  getLeaveRequestsDataDownload() {
    const statusList: ETimeoffStatus[] = []
    Object.keys(this.state.stateFilter).forEach((key) => {
      this.state.stateFilter[key] && statusList.push(ETimeoffStatus[key])
    })
    reportAction.downloadUserLeaveRequestsData(
      this.props.employee.Id,
      this.state.viewMode === EListCalendarViewModes.ListView ? this.state.dateFilter.StartDate : moment(this.state.referenceDateForCalendarView).utc(true).startOf("month").format(),
      this.state.viewMode === EListCalendarViewModes.ListView ? this.state.dateFilter.EndDate : moment(this.state.referenceDateForCalendarView).utc(true).endOf("month").format(),
      this.state.searchText,
      statusList
    )
  }

  render() {
    return (
      <div style={{ position: "relative" }}>
        {(this.props.loggedInUserType === ELoggedInUserType.Employee ||
          this.props.employee.Id === AuthenticationStore.getLoggedInUser().Id ||
          permissionHelper.hasPermission(AuthenticationStore.Permissions.LeaveTracking.AddUserLeaveTracking)
        ) &&
          this.props.employee.Active &&
          <FloatingActionButton data-intercom-target={"menu-Employees-LeaveTracking-Btn-Add"} className="fixed-add" style={{ position: "relative", float: "right", top: -45, right: 50, zIndex: 900 }} secondary={true} onClick={() => this.setState({ openLeaveRequest: true })}>
            <ContentAdd />
          </FloatingActionButton>
        }
        <TableActionBar
          onSearch={(searchText) => userActions.setLeaveTrackingSearchText(searchText)}
          searchText={this.state.searchText}
          rowSelectionCount={this.state.selectedLeaveRequests.length}
          singleSelectedMessage={strings.Leave_Request_Selected}
          multipleSelectedMessage={strings.x_Leave_Requests_Selected}
          title={strings.Leave_Tracking}
          onDeleteClick={() =>
            AppStateStore.setConfirmDialogContent({
              content: strings.Are_you_sure_you_want_to_delete_selected_leave_request,
              confirm: () => this.handleDelete(this.state.selectedLeaveRequests)
            })
          }
          exportTable={this.getLeaveRequestsDataDownload}
        />
        <div style={{ paddingLeft: 30, paddingTop: 10 }}>
          <ChooseListCalendarViewMode
            viewMode={this.state.viewMode}
            handleChange={viewMode => {
              localStorage.setItem("findd_ViewModeForLeaveTrackingPage", EnumValues.getNameFromValue(EListCalendarViewModes, viewMode))
              this.setState({ viewMode }, () => this.getLeaveRequests())
            }}
          />
        </div>
        {this.state.viewMode === EListCalendarViewModes.ListView ?
          <div>
            <LeaveTrackingFilter
              stateFilter={this.state.stateFilter}
              handleUpdateStateFilter={(stateFilter) => userActions.setLeaveTrackingStateFilter(stateFilter)}
              dateFilter={this.state.dateFilter}
              handleUpdateDateFilter={(dateFilter) => userActions.setLeaveTrackingDateFilter(dateFilter)}
            />
            <div style={{ paddingBottom: 20 }}>
              {this.state.loading ?
                <div className="spinner"><CircularProgress /></div> :
                <div>
                  {this.state.leaveRequestsSearch.Documents.length === 0 ?
                    <h2 style={{ textAlign: "center", marginTop: 25 }}>
                      {strings.No_leave_tracking_found}
                    </h2> :
                    <div>
                      {this.props.loggedInUserType !== ELoggedInUserType.Employee &&
                        (this.props.employee.Id === AuthenticationStore.getLoggedInUser().Id ?
                          permissionHelper.hasPermission(AuthenticationStore.Permissions.LeaveTracking.AllowSupervisorToApproveDenyOwnLeaveRequests) :
                          true
                        ) &&
                        permissionHelper.hasPermission(AuthenticationStore.Permissions.LeaveTracking.EditUserLeaveTracking) &&
                        <div style={{ textAlign: "right", paddingLeft: 10, paddingRight: 10, height: 48 }}>
                          {this.state.stateFilter.Requested && [
                            <IconButton
                              data-intercom-target={"menu-Employees-LeaveTracking-Btn-Approve"}
                              key="approve"
                              onClick={() => this.handleUpdateLeaveRequestsStatus(ETimeoffStatus.Approved, this.state.selectedLeaveRequests)}
                            >
                              <FontIcon className="fa fa-check" color={"rgb(97, 97, 97)"} />
                            </IconButton>,
                            <IconButton
                              data-intercom-target={"menu-Employees-LeaveTracking-Btn-Deny"}
                              key="deny"
                              onClick={() => this.handleUpdateLeaveRequestsStatus(ETimeoffStatus.Denied, this.state.selectedLeaveRequests)}
                            >
                              <FontIcon className="fa fa-remove" color={"rgb(97, 97, 97)"} />
                            </IconButton>
                          ]}
                        </div>
                      }
                      <UserDetailLeaveTrackingTable
                        employee={this.props.employee}
                        leaveRequests={this.state.leaveRequestsSearch.Documents}
                        selectedLeaveRequests={this.state.selectedLeaveRequests}
                        offset={this.state.offset}
                        limit={this.state.limit}
                        total={this.state.leaveRequestsSearch.TotalDocumentCount}
                        limitOption={UserStore.getLeaveTrackingLimitOption()}
                        loggedInUserType={this.props.loggedInUserType}
                        onPageClick={(offset) => userActions.setLeaveTrackingOffset(offset)}
                        onLimitChange={(limit) => userActions.setLeaveTrackingLimit(limit)}
                        handleUpdate={this.handleUpdateLeaveRequest}
                        handleSelect={(selectedLeaveRequests) => this.setState({ selectedLeaveRequests })}
                        handleNotesDrawer={leaveRequestForDrawerId => this.setState({ leaveRequestForDrawerId })}
                      />
                    </div>
                  }
                </div>
              }
            </div>
          </div> :
          <div>
            <div style={{ marginTop: 10 }}>
              <LeaveTrackingFilterForCalendarView
                referenceDate={this.state.referenceDateForCalendarView}
                stateFilter={this.state.stateFilter}
                handleUpdateStateFilter={stateFilter => userActions.setLeaveTrackingStateFilter(stateFilter)}
                handleUpdateDateFilter={dateFilter => this.setState({ referenceDateForCalendarView: dateFilter.StartDate }, () => this.getLeaveRequests())}
              />
            </div>
            <div style={{ padding: 20 }}>
              {!this.state.loading &&
                <LeaveTrackingCalendarView
                  pageName="UserDetailLeaveTracking"
                  loggedInUserType={this.props.loggedInUserType}
                  leaveRequests={this.state.leaveRequestsSearch.Documents}
                  refereceDate={moment(this.state.referenceDateForCalendarView).toDate()}
                  holidays={this.props.holidays}
                  handleUpdate={this.handleUpdateLeaveRequest}
                  updateStatusCb={this.updateStatusCb}
                  handleDelete={leaveRequest => leaveRequest.AppliedInPayroll ?
                    AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_leave_request_applied_in_payroll) :
                    AppStateStore.setConfirmDialogContent({
                      content: strings.formatString(
                        strings.Are_you_sure_you_want_to_delete_leave_request_of_employee,
                        leaveRequest.EmployeeName,
                        moment(timeHelper.keepConvertUTCToLocal(leaveRequest.StartDate)).format("DD/MM/YYYY") === moment(timeHelper.keepConvertUTCToLocal(leaveRequest.EndDate)).format("DD/MM/YYYY") ?
                          moment(timeHelper.keepConvertUTCToLocal(leaveRequest.StartDate)).format("DD/MM/YYYY") :
                          `${moment(timeHelper.keepConvertUTCToLocal(leaveRequest.StartDate)).format("DD/MM/YYYY")} - ${moment(timeHelper.keepConvertUTCToLocal(leaveRequest.EndDate)).format("DD/MM/YYYY")}`
                      ) as string,
                      confirm: () => this.handleDelete([leaveRequest])
                    })
                  }
                />
              }
            </div>
          </div>
        }
        {this.state.openLeaveRequest &&
          <AddLeaveRequestDialog
            employee={this.props.employee}
            editLeaveRequest={this.state.editLeaveRequest}
            handleClose={() => this.setState({ openLeaveRequest: false, editLeaveRequest: null, disableRequestFields: false })}
            handleAdd={this.handleAdd}
            handleUpdate={this.update}
            loggedInUserType={this.props.loggedInUserType}
            readonly={this.state.disableRequestFields}
          />
        }
        {!this.state.loading &&
          <LeaveTrackingNotesDrawer
            open={this.state.leaveRequestForDrawerId ? true : false}
            leaveRequestId={this.state.leaveRequestForDrawerId}
            handleClose={() => this.setState({ leaveRequestForDrawerId: null })}
          />
        }
      </div>
    )
  }
}